import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';
import { respondToGroupInvitation } from './friendsSlice';

// Safe stringify to avoid circular structure errors in logs
const safeStringify = (obj: any, fallback = ''): string => {
  try {
    const seen = new WeakSet();
    return JSON.stringify(obj, function (_key, value) {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      if (typeof value === 'function') return value.toString();
      return value;
    }, 2);
  } catch (e) {
    try {
      if (obj instanceof Error) {
        return JSON.stringify({ message: obj.message, stack: (obj as any).stack }, null, 2);
      }
    } catch (e2) {
      // ignore
    }
    try { return String(obj); } catch (e3) { return fallback; }
  }
};

// 類型定義
export interface Travel {
  id: string;
  name: string;
  description: string | null;
  background_image_url: string | null;
  start_date: string | null;
  end_date: string | null;
  budget_per_person: number | null;
  currency: string;
  payment_mode: string | null;
  status: string;
  visibility: string;
  creator_id: string;
  location: string | null; // 位置信息
  min_participants: number | null;
  max_participants: number | null;
  created_at: string;
  updated_at: string;
  settings?: any; // 添加settings字段，包含Google地图数据等
  // 扩展字段用于首页显示
  members?: TravelMember[];
  user_role?: string;
  joined_at?: string;
}

export interface TravelMember {
  id: string;
  group_id: string;
  user_id: string;
  role: string;
  share_ratio: number;
  status: string;
  joined_at: string;
  // 關聯數據
  profile?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface CardTemplateSummary {
  id: string;
  card_type: 'poi' | 'restaurant' | 'transport' | 'lodging' | 'activity' | 'shopping' | string;
  title: string;
  description: string | null;
  location: string | null;
  base_payload: any;
  status?: string | null;
  tags?: string[] | null;
  category?: string | null;
  subcategory?: string | null;
  price_range?: string | null;
  cover_image?: string | null;
  image_gallery?: any;
  rating?: number | null;
  review_count?: number | null;
  address?: string | null;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  opening_hours?: any;
  best_season?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface ItineraryEntry {
  id: string;
  group_id: string;
  day_number: number;
  day_date: string | null;
  sort_order: number;
  item_type: 'card' | 'transport' | 'note' | 'custom' | string;
  card_id: string | null;
  note: string | null;
  start_time: string | null;
  end_time: string | null;
  creator_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  parent_entry_id?: string | null;
  child_sort?: number | null;
  custom_payload: Record<string, any>;
  inline_payload: any;
  card?: CardTemplateSummary | null;
}

export interface TravelDetail extends Travel {
  members: TravelMember[];
  itinerary_entries?: ItineraryEntry[];
  itinerary_summary?: Record<string, any> | null;
}

export interface TravelJoinRequest {
  id: string;
  group_id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  message: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    display_name: string | null;
    avatar_url: string | null;
  };
}

export interface TravelsState {
  travels: Travel[];
  visibleTravels: Travel[];
  currentTravel: TravelDetail | null;
  // POI功能已移除
  currentTravelTodos: any[];
  joinRequests: TravelJoinRequest[];
  loading: boolean;
  error: string | null;
}

// 初始狀態
const initialState: TravelsState = {
  travels: [],
  visibleTravels: [],
  currentTravel: null,
  // POI功能已移除
  currentTravelTodos: [],
  joinRequests: [],
  loading: false,
  error: null,
};

// 異步操作：獲取我的拼游（包括创建和参与的）
export const getMyTravels = createAsyncThunk(
  'travels/getMyTravels',
  async (userId: string, { rejectWithValue }) => {
    try {
      console.log('getMyTravels called for user:', userId);
      
      // 先获取用户参与的所有项目ID（包括作为创建者和成员）
      const { data: memberGroups, error: memberError } = await supabase
        .from('group_members')
        .select('group_id, role, joined_at, status')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (memberError) {
        console.error('Error fetching member groups:', memberError);
        throw memberError;
      }

      console.log('Member groups found:', memberGroups?.length || 0);
      
      if (!memberGroups || memberGroups.length === 0) {
        console.log('No member groups found for user');
        return [];
      }

      // 记录找到的所有项目ID
      const groupIds = memberGroups.map(mg => mg.group_id);
      console.log('Group IDs found:', groupIds);

      // 获取项目的详细信息
      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select('*')
        .in('id', groupIds)
        .order('updated_at', { ascending: false });

      if (groupsError) {
        console.error('Error fetching groups details:', groupsError);
        throw groupsError;
      }

      console.log('Groups details fetched:', groups?.length || 0);
      if (!groups || groups.length === 0) {
        console.log('No groups details found');
        return [];
      }

      // 为每个项目获取成员列表（包含profile信息）
      const groupsWithMembers = await Promise.all(
        (groups || []).map(async (group) => {
          const { data: members, error: membersError } = await supabase
            .from('group_members')
            .select('*')
            .eq('group_id', group.id)
            .eq('status', 'active');

          if (membersError) {
            console.error(`Error fetching members for group ${group.id}:`, membersError);
          }

          console.log(`Group ${group.id} (${group.name}) has ${members?.length || 0} members`);

          const membersWithProfiles = await Promise.all(
            (members || []).map(async (member) => {
              const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('display_name, avatar_url')
                .eq('id', member.user_id)
                .maybeSingle(); // 使用 maybeSingle() 替代 single()

              if (profileError) {
                console.error(`Error fetching profile for user ${member.user_id}:`, {
                  code: profileError.code,
                  message: profileError.message,
                  details: profileError.details,
                  hint: profileError.hint
                });
                // 如果获取某个成员的资料失败，我们就跳过这个成员
                return null;
              }

              // 如果用户profile不存在，使用默认值
              if (!profileData) {
                console.warn(`Profile not found for user ${member.user_id}, using default values`);
                return {
                  ...member,
                  profile: {
                    display_name: '未知用户',
                    avatar_url: null,
                  },
                };
              }

              return {
                ...member,
                profile: profileData || null,
              };
            })
          );

          // 过滤掉null成员，避免UI渲染错误
          const validMembers = membersWithProfiles.filter(member => member !== null);

          // 添加用户在此项目中的角色
          const userMembership = memberGroups.find(mg => mg.group_id === group.id);
          console.log(`User role in group ${group.id}: ${userMembership?.role || 'unknown'}`);

          return {
            ...group,
            members: validMembers,
            user_role: userMembership?.role || 'member',
            joined_at: userMembership?.joined_at
          };
        })
      );

      console.log('Final travels with members:', groupsWithMembers.length);
      return groupsWithMembers as Travel[];
    } catch (error: any) {
      console.error('getMyTravels failed:', error.message);
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：獲取拼游詳情
export const getTravelDetail = createAsyncThunk(
  'travels/getTravelDetail',
  async (travelId: string, { rejectWithValue }) => {
    try {
      console.log('获取拼游详情，ID:', travelId);
      
      // 獲取拼游基本信息
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .select('*')
        .eq('id', travelId)
        .single();

      if (groupError) {
        console.error('获取拼游信息失败:', groupError);
        throw groupError;
      }
      
      console.log('拼游信息:', group);

      // 獲取拼游成员 - 使用简单查询避免关系问题
      const { data: members, error: membersError } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', travelId);

      if (membersError) {
        console.error('获取成员信息失败:', membersError);
        throw membersError;
      }
      
      console.log('成员信息:', members);

      // 为每个成员获取profile信息
      const membersWithProfiles = await Promise.all(
        (members || []).map(async (member) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url')
            .eq('id', member.user_id)
            .single();

          return {
            ...member,
            profile: profileData || null,
          };
        })
      );

      const baseEntryColumns = `
          id,
          group_id,
          day_number,
          day_date,
          sort_order,
          item_type,
          card_id,
          note,
          start_time,
          end_time,
          parent_entry_id,
          child_sort,
          custom_payload,
          inline_payload
        `;
      const optionalEntryColumns = ', creator_id, created_at, updated_at';

      let itineraryEntries: any[] | null = null;
      let itineraryError: any = null;

      const { data: entriesWithOptional, error: entriesWithOptionalError } = await supabase
        .from('group_itinerary_entries')
        .select(`${baseEntryColumns}${optionalEntryColumns}`)
        .eq('group_id', travelId)
        .order('day_number', { ascending: true })
        .order('sort_order', { ascending: true });

      if (entriesWithOptionalError) {
        const errCode = (entriesWithOptionalError as any)?.code;
        const errMessage = String((entriesWithOptionalError as any)?.message || '');
        const missingColumn = errCode === '42703' || /column .* does not exist/i.test(errMessage);
        if (missingColumn) {
          console.warn('[travelsSlice] creator metadata columns missing in group_itinerary_entries, retrying without them');
          const { data: entriesFallback, error: entriesFallbackError } = await supabase
            .from('group_itinerary_entries')
            .select(baseEntryColumns)
            .eq('group_id', travelId)
            .order('day_number', { ascending: true })
            .order('sort_order', { ascending: true });
          itineraryEntries = entriesFallback || [];
          itineraryError = entriesFallbackError || null;
        } else {
          itineraryEntries = entriesWithOptional || [];
          itineraryError = entriesWithOptionalError;
        }
      } else {
        itineraryEntries = entriesWithOptional || [];
      }

      if (itineraryError) {
        console.error('获取行程条目失败:', itineraryError);
        throw itineraryError;
      }

      const cardIds = (itineraryEntries || [])
        .map((entry: any) => entry.card_id)
        .filter((id: string | null | undefined) => Boolean(id)) as string[];

      let cardMap = new Map<string, CardTemplateSummary>();
      if (cardIds.length > 0) {
        const uniqueIds = Array.from(new Set(cardIds));
        const { data: cards, error: cardsError } = await supabase
          .from('cards_cards')
          .select(`
            id,
            card_type,
            title,
            description,
            location,
            base_payload,
            status,
            tags,
            category,
            subcategory,
            price_range,
            cover_image,
            image_gallery,
            rating,
            review_count,
            address,
            phone,
            website,
            email,
            opening_hours,
            best_season,
            latitude,
            longitude
          `)
          .in('id', uniqueIds);

        if (cardsError) {
          console.error('获取卡片信息失败:', cardsError);
          throw cardsError;
        }

        cardMap = new Map(
          (cards || []).map((card: any) => [card.id, card as CardTemplateSummary])
        );
      }

      const normalizedEntries: ItineraryEntry[] = (itineraryEntries || []).map((entry: any) => ({
        id: entry.id,
        group_id: entry.group_id,
        day_number: entry.day_number,
        day_date: entry.day_date,
        sort_order: entry.sort_order,
        item_type: entry.item_type,
        card_id: entry.card_id,
        note: entry.note,
        start_time: entry.start_time,
        end_time: entry.end_time,
        creator_id: entry.creator_id ?? null,
        created_at: entry.created_at ?? null,
        updated_at: entry.updated_at ?? null,
        parent_entry_id: entry.parent_entry_id ?? null,
        child_sort: entry.child_sort ?? null,
        custom_payload: entry.custom_payload || {},
        inline_payload: entry.inline_payload ?? null,
        card: entry.card_id ? cardMap.get(entry.card_id) || null : null,
      }));

      const result = {
        ...group,
        members: membersWithProfiles || [],
        itinerary_entries: normalizedEntries,
        itinerary_summary: group.itinerary_summary || null,
      } as TravelDetail;
      
      console.log('最终结果:', result);
      
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：創建拼游
export const createTravel = createAsyncThunk(
  'travels/createTravel',
  async (
    {
      travelData,
      userId,
      // POI功能已移除
      todoData = [],
    }: {
      travelData: {
        name: string;
        description?: string;
        start_date?: string;
        end_date?: string;
        budget_per_person?: number;
        currency?: string;
        status?: string;
        visibility?: string;
        background_image_url?: string;
        location?: string;  // 位置信息
        min_participants?: number;  // 最小参与人数
        max_participants?: number;  // 最大参与人数
        payment_mode?: string;  // 支付模式
      };
      userId: string;
      // POI功能已移除
      todoData?: Array<{
        title: string;
        description?: string;
        completed?: boolean;
      }>;
    },
    { rejectWithValue }
  ) => {
    try {
  // 創建拼游
  console.log('Creating travel with data:', safeStringify(travelData));
      
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert({
          ...travelData,
          creator_id: userId,
        })
        .select()
        .single();

      if (groupError) {
        console.error('Error creating travel group:', groupError);
        throw groupError;
      }

      console.log('Group created successfully:', group);

      // 將創建者添加為拼游成員
      console.log('Adding creator as member, group_id:', group.id, 'user_id:', userId);
      
      const { data: memberData, error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: group.id,
          user_id: userId,
          role: 'creator',
          share_ratio: 1.0,
          status: 'active',
        })
        .select();

      if (memberError) {
        console.error('Error adding creator as member:', memberError);
        throw memberError;
      }

      console.log('Creator added as member successfully:', memberData);

      // POI功能已移除，不再处理POI数据

      // 自动创建预设待办 (从todo_templates表读取)
      try {
        console.log('[createTravel] 开始从数据库读取待办模板...');
        
        // 从数据库读取激活的模板
        const { data: templates, error: templatesError } = await supabase
          .from('todo_templates')
          .select('*')
          .eq('is_active', true)
          .order('display_order', { ascending: true });
        
        if (templatesError) {
          console.error('[createTravel] 读取模板失败:', templatesError);
        } else if (templates && templates.length > 0) {
          console.log('[createTravel] 读取到待办模板:', templates.length, '个');
          
          // 从目的地字段提取国家代码
          const extractCountryCode = () => {
            const location = travelData.location;
            if (!location) return null;
            
            // 匹配格式: "Tokyo, Japan" -> "JP"
            const countryMap: Record<string, string> = {
              'Japan': 'JP',
              'United States': 'US',
              'USA': 'US',
              'United Kingdom': 'UK',
              'UK': 'UK',
              'China': 'CN',
              'Thailand': 'TH',
              'Singapore': 'SG',
              'Hong Kong': 'HK',
              'Taiwan': 'TW',
              'Korea': 'KR',
              'South Korea': 'KR',
            };
            
            for (const [country, code] of Object.entries(countryMap)) {
              if (location.includes(country)) {
                return code;
              }
            }
            
            // 匹配格式: "JP-Tokyo" -> "JP"
            const match = location.match(/^([A-Z]{2})-/);
            if (match) {
              return match[1];
            }
            
            return null;
          };
          
          const countryCode = extractCountryCode();
          console.log('[createTravel] 提取到国家代码:', countryCode, '来源:', travelData.location);
          
          // 筛选适用的模板
          const applicableTemplates = templates.filter(template => {
            // 通用模板始终适用
            if (template.template_type === 'universal') {
              return true;
            }
            
            // 地区特定模板需要匹配目的地
            if (template.template_type === 'region_specific' && template.target_regions) {
              const regions = Array.isArray(template.target_regions) 
                ? template.target_regions 
                : [];
              return countryCode && regions.includes(countryCode);
            }
            
            return false;
          });
          
          console.log('[createTravel] 筛选后适用模板:', applicableTemplates.length, '个');
          
          if (applicableTemplates.length > 0) {
            // 批量创建待办
            const todoInserts = applicableTemplates.map(template => ({
              group_id: group.id,
              title: template.title,
              description: template.description,
              category: template.category || 'general',
              priority: template.priority || 'medium',
              completed: false,
              metadata: {
                isAd: template.is_ad,
                adPartner: template.ad_partner,
                adUrl: template.ad_url,
                adCampaign: template.ad_campaign,
                cta: template.ad_cta,
                templateKey: template.template_key,
              },
              created_by: userId,
            }));
            
            console.log('[createTravel] 准备插入待办:', todoInserts);
            
            const { data: todosData, error: todosError } = await supabase
              .from('travel_todos')
              .insert(todoInserts)
              .select();
            
            if (todosError) {
              console.error('[createTravel] 创建预设待办失败:', todosError);
            } else {
              console.log('[createTravel] ✅ 成功创建预设待办:', todosData?.length, '条');
            }
          }
        }
      } catch (templateError) {
        console.error('[createTravel] 待办模板处理异常:', templateError);
        // 不抛出错误,todo创建失败不应阻止旅行创建
      }

      // 如果有待办事项数据，插入到group_todos表 (保留向后兼容)
      if (todoData && todoData.length > 0) {
        console.log('Inserting todo data:', todoData);
        
        const todoInsertData = todoData.map((todo: any) => ({
          group_id: group.id,
          title: todo.title,
          description: todo.description,
          category: todo.category || 'general',
          priority: todo.priority || 'medium',
          status: todo.completed ? 'completed' : 'pending',
          creator_id: userId,
        }));

        const { data: todoResult, error: todoError } = await supabase
          .from('group_todos')
          .insert(todoInsertData)
          .select();

        if (todoError) {
          console.error('Error inserting todo data:', todoError);
          // 不抛出错误，因为todo数据不是必需的
        } else {
          console.log('Todo data inserted successfully:', todoResult);
        }
      }

      return group as Travel;
    } catch (error: any) {
      console.error('Create travel failed with error:', error);
      console.error('Error details:', safeStringify(error));
      return rejectWithValue(error.message || 'Unknown error creating travel');
    }
  }
);

// 異步操作：邀請成員
export const inviteMembers = createAsyncThunk(
  'travels/inviteMembers',
  async (
    {
      travelId,
      emails,
    }: {
      travelId: string;
      emails: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      // 查找這些電子郵件對應的用戶
      const { data: users, error: usersError } = await supabase
        .from('auth.users')
        .select('id, email')
        .in('email', emails);

      if (usersError) throw usersError;

      if (!users || users.length === 0) {
        throw new Error('未找到對應的用戶');
      }

      // 為每個找到的用戶創建拼游成員記錄
      const memberInserts = users.map(user => ({
        group_id: travelId,
        user_id: user.id,
        role: 'member',
        share_ratio: 1.0,
        status: 'pending'
      }));

      const { error: insertError } = await supabase
        .from('group_members')
        .insert(memberInserts);

      if (insertError) throw insertError;

      return { success: true, invited: users.length };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：更新成員份額
export const updateMemberShare = createAsyncThunk(
  'travels/updateMemberShare',
  async (
    {
      travelId,
      userId,
      shareRatio,
    }: {
      travelId: string;
      userId: string;
      shareRatio: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { data, error } = await supabase
        .from('group_members')
        .update({ share_ratio: shareRatio })
        .eq('group_id', travelId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;
      return data as TravelMember;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：獲取可見的拼游（好友可見和公开拼游）
export const getVisibleTravels = createAsyncThunk(
  'travels/getVisibleTravels',
  async (userId: string, { rejectWithValue }) => {
    try {
      console.log('getVisibleTravels called for user:', userId);
      
      // 獲取好友列表 - 需要查询双向关系
      const [friendsData1, friendsData2] = await Promise.all([
        // 查询 user_id = userId 的好友关系
        supabase
          .from('friends')
          .select('friend_id')
          .eq('user_id', userId),
        // 查询 friend_id = userId 的好友关系（反向）
        supabase
          .from('friends')
          .select('user_id')
          .eq('friend_id', userId)
      ]);

      if (friendsData1.error) {
        console.error('Error fetching friends (direction 1):', friendsData1.error);
        throw friendsData1.error;
      }

      if (friendsData2.error) {
        console.error('Error fetching friends (direction 2):', friendsData2.error);
        throw friendsData2.error;
      }

      // 合并两个方向的好友ID
      const friendIds1 = friendsData1.data?.map(f => f.friend_id) || [];
      const friendIds2 = friendsData2.data?.map(f => f.user_id) || [];
      const friendIds = [...new Set([...friendIds1, ...friendIds2])]; // 去重
      
      console.log('User friends (bidirectional):', friendIds.length, friendIds);

      // 獲取可見的拼游：
      // 1. 公开拼游
      // 2. 好友創建的好友可見拼游
      let query = supabase
        .from('groups')
        .select('*')
        .neq('creator_id', userId); // 排除用戶自己創建的拼游

      if (friendIds.length > 0) {
        // 公开拼游 OR (好友可見 AND 是好友創建的)
        query = query.or(`visibility.eq.public,and(visibility.eq.friends_only,creator_id.in.(${friendIds.join(',')}))`);
      } else {
        // 只獲取公开拼游
        query = query.eq('visibility', 'public');
      }

      const { data: visibleGroups, error: groupsError } = await query
        .order('updated_at', { ascending: false });

      if (groupsError) {
        console.error('Error fetching visible travels:', groupsError);
        throw groupsError;
      }

      console.log('Visible travels found:', visibleGroups?.length || 0);
      
      // 首先獲取用戶已加入的所有項目ID
      const { data: userMemberships, error: membershipError } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', userId)
        .eq('status', 'active');

      const userJoinedGroupIds = userMemberships?.map(m => m.group_id) || [];
      console.log('User already joined groups:', userJoinedGroupIds);

      // 過濾掉用戶已加入的項目和已結束的項目
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 重置时间部分，只比较日期
      
      const filteredGroups = (visibleGroups || []).filter(group => {
        // 过滤已加入的项目
        if (userJoinedGroupIds.includes(group.id)) {
          return false;
        }
        
        // 过滤已结束的项目
        if (group.end_date) {
          const endDate = new Date(group.end_date);
          endDate.setHours(23, 59, 59, 999); // 结束日期的最后一刻
          if (today > endDate) {
            console.log(`过滤已结束项目: ${group.name} (结束日期: ${group.end_date})`);
            return false;
          }
        }
        
        // 过滤已取消的项目
        if (group.status === 'cancelled' || group.status === 'completed') {
          console.log(`过滤已完成/取消项目: ${group.name} (状态: ${group.status})`);
          return false;
        }
        
        return true;
      });
      
      console.log('Filtered visible travels (excluding joined):', filteredGroups.length);
      
      // 為每個拼游獲取創建者信息
      const groupsWithCreators = await Promise.all(
        filteredGroups.map(async (group) => {
          const { data: creatorProfile, error: creatorError } = await supabase
            .from('profiles')
            .select('display_name, avatar_url')
            .eq('id', group.creator_id)
            .maybeSingle();

          if (creatorError) {
            console.error(`Error fetching creator for travel ${group.id}:`, creatorError);
          }

          // 由於已經過濾掉用戶加入的項目，這裡不需要再檢查成員狀態

          // 檢查是否有待處理的加入申請
          const { data: requestCheck, error: requestError } = await supabase
            .from('group_join_requests')
            .select('id, status')
            .eq('group_id', group.id)
            .eq('user_id', userId)
            .eq('status', 'pending')
            .single();

          // 獲取項目成員信息
          const { data: members, error: membersError } = await supabase
            .from('group_members')
            .select('user_id, role, status')
            .eq('group_id', group.id)
            .eq('status', 'active');

          let membersWithProfiles = [];
          if (members && !membersError && members.length > 0) {
            try {
              membersWithProfiles = await Promise.all(
                members.map(async (member) => {
                  const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('display_name, avatar_url')
                    .eq('id', member.user_id)
                    .maybeSingle();

                  if (profileError) {
                    console.warn(`获取成员 ${member.user_id} 的资料失败:`, profileError);
                  }

                  return {
                    user_id: member.user_id,
                    role: member.role,
                    status: member.status,
                    profile: profileData || {
                      display_name: '未知用户',
                      avatar_url: null,
                    },
                  };
                })
              );
            } catch (error) {
              console.error(`获取项目 ${group.id} 成员资料时出错:`, error);
              membersWithProfiles = [];
            }
          }

          return {
            ...group,
            creator_profile: creatorProfile || null,
            is_member: false, // 已經過濾掉加入的項目，所以這裡總是false
            has_pending_request: !!requestCheck,
            members: membersWithProfiles,
          };
        })
      );

      return groupsWithCreators;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：申請加入拼游
export const requestToJoinTravel = createAsyncThunk(
  'travels/requestToJoinTravel',
  async (
    {
      travelId,
      userId,
      message
    }: {
      travelId: string;
      userId: string;
      message?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      console.log('travelsSlice: requestToJoinTravel called', { travelId, userId, message });

      // 首先获取拼游信息，检查申请截止日期
      const { data: travelInfo, error: travelError } = await supabase
        .from('groups')
        .select('start_date, name')
        .eq('id', travelId)
        .single();

      if (travelError) {
        throw new Error('获取拼游信息失败');
      }

      // 检查申请截止日期（开始日期当天）
      if (travelInfo.start_date) {
        const today = new Date();
        const startDate = new Date(travelInfo.start_date);

        // 重置时间部分，只比较日期
        today.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);

        if (today > startDate) {
          const startDateStr = startDate.toLocaleDateString('zh-CN');
          throw new Error(`该拼游已于 ${startDateStr} 开始，申请已截止`);
        }
      }

      // 檢查是否已經是成員
      const { data: existingMember, error: memberError } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', travelId)
        .eq('user_id', userId)
        .single();

      console.log('travelsSlice: member check result', { existingMember, memberError });

      if (existingMember) {
        throw new Error('您已經是此拼游的成員');
      }

      // 获取项目信息，检查可见性
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('visibility, name, creator_id')
        .eq('id', travelId)
        .single();

      if (groupError) throw groupError;

      console.log('travelsSlice: group visibility check', { visibility: groupData.visibility });

      // 根据可见性决定加入方式
      if (groupData.visibility === 'private') {
        // 私密项目需要申请
        // 檢查是否已有待處理的申請
        const { data: existingRequest, error: requestError } = await supabase
          .from('group_join_requests')
          .select('id, status')
          .eq('group_id', travelId)
          .eq('user_id', userId)
          .eq('status', 'pending')
          .single();

        if (existingRequest) {
          throw new Error('您已經提交過加入申請，請等待審核');
        }

        // 提交申請
        console.log('travelsSlice: submitting join request for private group');
        const { data: request, error: submitError } = await supabase
          .from('group_join_requests')
          .insert({
            group_id: travelId,
            user_id: userId,
            message: message || null,
          })
          .select()
          .single();

        if (submitError) throw submitError;

        console.log('travelsSlice: join request submitted successfully', request);
        return { type: 'request', data: request };
      } else {
        // 公开或朋友可见项目直接加入
        console.log('travelsSlice: directly joining public/friends group');
        const { data: member, error: joinError } = await supabase
          .from('group_members')
          .insert({
            group_id: travelId,
            user_id: userId,
            role: 'member'
            // joined_at 有默认值 now()，不需要手动设置
          })
          .select()
          .single();

        if (joinError) throw joinError;

        console.log('travelsSlice: successfully joined group', member);
        return { type: 'joined', data: member };
      }
    } catch (error: any) {
      console.error('travelsSlice: requestToJoinTravel error', error);
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：處理加入申請
export const handleJoinRequest = createAsyncThunk(
  'travels/handleJoinRequest',
  async (
    {
      requestId,
      action,
      travelId
    }: {
      requestId: string;
      action: 'approve' | 'reject';
      travelId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // 更新申請狀態
      const { data: request, error: updateError } = await supabase
        .from('group_join_requests')
        .update({ 
          status: action === 'approve' ? 'approved' : 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .select()
        .single();

      if (updateError) throw updateError;

      // 如果批准，將用戶添加到拼游成員
      if (action === 'approve') {
        const { error: memberError } = await supabase
          .from('group_members')
          .insert({
            group_id: request.group_id,
            user_id: request.user_id,
            role: 'member',
            share_ratio: 1.0,
            status: 'active',
          });

        if (memberError) throw memberError;
      }

      return request;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：獲取拼游的加入申請
export const getTravelJoinRequests = createAsyncThunk(
  'travels/getTravelJoinRequests',
  async (travelId: string, { rejectWithValue }) => {
    try {
      console.log('🔍 正在查询申请，travelId:', travelId);
      
      // 1. 先查询申请表
      const { data: requests, error } = await supabase
        .from('group_join_requests')
        .select('*')
        .eq('group_id', travelId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      console.log('📝 申请查询结果:', { requests, error, count: requests?.length || 0 });

      if (error) {
        console.error('❌ 查询申请失败:', error);
        throw error;
      }

      if (!requests || requests.length === 0) {
        console.log('✅ 没有待处理申请');
        return [];
      }

      // 2. 获取所有申请用户的信息
      const userIds = [...new Set(requests.map(request => request.user_id))];
      console.log('🔍 查询用户信息，userIds:', userIds);
      
      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', userIds);

      console.log('📝 用户查询结果:', { usersData, usersError, count: usersData?.length || 0 });

      let processedUsersData: Array<{id: string; display_name: string | null; avatar_url: string | null}> = [];
      if (usersError) {
        console.warn('⚠️ 获取用户信息失败:', usersError);
        // 即使获取用户信息失败，也创建基本的用户信息
        processedUsersData = userIds.map(id => ({
          id,
          display_name: null,
          avatar_url: null
        }));
      } else {
        processedUsersData = usersData || [];
        
        // 确保所有需要的用户ID都有对应的用户信息
        userIds.forEach(id => {
          if (!processedUsersData.find(user => user.id === id)) {
            processedUsersData.push({
              id,
              display_name: null,
              avatar_url: null
            });
          }
        });
      }

      // 3. 合并数据
      const requestsWithUsers = requests.map(request => ({
        ...request,
        profiles: processedUsersData.find(user => user.id === request.user_id) || {
          id: request.user_id,
          display_name: null,
          avatar_url: null
        }
      }));

      console.log('✅ 返回合并后的申请数据:', requestsWithUsers.length, '条');
      return requestsWithUsers;
    } catch (error: any) {
      console.error('💥 获取申请异常:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：更新拼游信息
export const updateTravel = createAsyncThunk(
  'travels/updateTravel',
  async (
    {
      travelId,
      updates,
    }: {
      travelId: string;
      updates: {
        budget_per_person?: number;
        payment_mode?: string;
        currency?: string;
        [key: string]: any;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      console.log('更新拼游信息:', { travelId, updates });
      
      const { data: group, error } = await supabase
        .from('groups')
        .update(updates)
        .eq('id', travelId)
        .select()
        .single();

      if (error) {
        console.error('更新拼游失败:', error);
        throw error;
      }

      console.log('拼游更新成功:', group);
      return group;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 異步操作：删除拼游项目
export const deleteTravel = createAsyncThunk(
  'travels/deleteTravel',
  async (
    {
      travelId,
      userId,
      isAdmin = false,
    }: {
      travelId: string;
      userId: string;
      isAdmin?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      console.log('🗑️ 开始删除拼游项目:', { travelId, userId, isAdmin });
      
      // 1. 首先检查权限
      if (!isAdmin) {
        // 非管理员需要验证是否为项目创建者
        const { data: group, error: groupError } = await supabase
          .from('groups')
          .select('creator_id, name')
          .eq('id', travelId)
          .single();

        if (groupError) {
          console.error('❌ 获取项目信息失败:', groupError);
          throw new Error('获取项目信息失败');
        }

        if (group.creator_id !== userId) {
          throw new Error('权限不足：只有项目创建者才能删除项目');
        }
        
        console.log('✅ 权限验证通过，项目创建者可以删除项目:', group.name);
      } else {
        console.log('✅ 管理员权限，可以删除任何项目');
      }

      // 2. 开始删除关联数据（按依赖关系顺序删除）

      // 删除费用收据
      console.log('🧹 删除费用收据...');
      const { error: receiptsError } = await supabase
        .from('expense_receipts')
        .delete()
        .eq('group_id', travelId);

      if (receiptsError) {
        console.warn('⚠️ 删除费用收据时出错:', receiptsError);
      }

      // 删除群组消息
      console.log('🧹 删除群组消息...');
      const { error: messagesError } = await supabase
        .from('group_messages')
        .delete()
        .eq('group_id', travelId);
      
      if (messagesError) {
        console.warn('⚠️ 删除群组消息时出错:', messagesError);
      }

      // 删除群组待办相关（先删除依赖表）
      console.log('🧹 删除群组待办日志...');
      // 先获取该群组的所有待办ID
      const { data: todoIds } = await supabase
        .from('group_todos')
        .select('id')
        .eq('group_id', travelId);
      
      if (todoIds && todoIds.length > 0) {
        const { error: todoLogsError } = await supabase
          .from('group_todo_logs')
          .delete()
          .in('todo_id', todoIds.map(t => t.id));
        
        if (todoLogsError) {
          console.warn('⚠️ 删除群组待办日志时出错:', todoLogsError);
        }
      }

      // 删除群组待办
      console.log('🧹 删除群组待办...');
      const { error: todosError } = await supabase
        .from('group_todos')
        .delete()
        .eq('group_id', travelId);
      
      if (todosError) {
        console.warn('⚠️ 删除群组待办时出错:', todosError);
      }

      // 删除加入申请
      console.log('🧹 删除加入申请...');
      const { error: joinRequestsError } = await supabase
        .from('group_join_requests')
        .delete()
        .eq('group_id', travelId);
      
      if (joinRequestsError) {
        console.warn('⚠️ 删除加入申请时出错:', joinRequestsError);
      }

      // 删除群组邀请
      console.log('🧹 删除群组邀请...');
      const { error: invitationsError } = await supabase
        .from('group_invitations')
        .delete()
        .eq('group_id', travelId);
      
      if (invitationsError) {
        console.warn('⚠️ 删除群组邀请时出错:', invitationsError);
      }

      // 删除群组成员
      console.log('🧹 删除群组成员...');
      const { error: membersError } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', travelId);
      
      if (membersError) {
        console.warn('⚠️ 删除群组成员时出错:', membersError);
      }

      // 最后删除群组主记录
      console.log('🧹 删除群组主记录...');
      const { error: groupError } = await supabase
        .from('groups')
        .delete()
        .eq('id', travelId);

      if (groupError) {
        console.error('❌ 删除群组主记录失败:', groupError);
        throw new Error('删除群组失败: ' + groupError.message);
      }

      console.log('✅ 拼游项目删除成功');
      return { travelId, success: true };
      
    } catch (error: any) {
      console.error('❌ 删除拼游项目失败:', error);
      return rejectWithValue(error.message || '删除拼游项目失败');
    }
  }
);

// 異步操作：標記項目為已結束（軟刪除）
export const markTravelAsCompleted = createAsyncThunk(
  'travels/markTravelAsCompleted',
  async (
    {
      travelId,
      userId,
    }: {
      travelId: string;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      console.log('📝 标记拼游项目为已结束:', { travelId, userId });
      
      // 检查权限
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .select('creator_id, name')
        .eq('id', travelId)
        .single();

      if (groupError) {
        throw new Error('获取项目信息失败');
      }

      if (group.creator_id !== userId) {
        throw new Error('权限不足：只有项目创建者才能操作');
      }

      // 更新状态为已完成
      const { data: updatedGroup, error: updateError } = await supabase
        .from('groups')
        .update({
          status: 'completed',
          updated_at: new Date().toISOString(),
        })
        .eq('id', travelId)
        .select()
        .single();

      if (updateError) {
        throw new Error('更新项目状态失败: ' + updateError.message);
      }

      console.log('✅ 项目已标记为已结束:', updatedGroup.name);
      return updatedGroup;
      
    } catch (error: any) {
      console.error('❌ 标记项目为已结束失败:', error);
      return rejectWithValue(error.message || '操作失败');
    }
  }
);

// POI功能已移除 - getTravelPOIs 函数已删除

// 獲取拼游待办事项数据
export const getTravelTodos = createAsyncThunk(
  'travels/getTravelTodos',
  async (groupId: string, { rejectWithValue }) => {
    try {
      // NOTE: mobile app and other slices use `travel_todos` table. Use the same table here.
      const { data, error } = await supabase
        .from('travel_todos')
        .select('*')
        .eq('group_id', groupId)
  .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching travel todos:', error);
        throw error;
      }

      // 如果需要创建者信息，单独查询
      const todosWithCreators = await Promise.all(
        (data || []).map(async (todo) => {
          if (todo.created_by) {
            const { data: creatorData, error: creatorError } = await supabase
              .from('profiles')
              .select('id, display_name, avatar_url')
              .eq('id', todo.created_by)
              .single();
            
            if (!creatorError && creatorData) {
              return {
                ...todo,
                creator: creatorData
              };
            }
          }
          return todo;
        })
      );

      return todosWithCreators;
    } catch (error: any) {
      console.error('Get travel todos failed:', error);
      return rejectWithValue(error.message);
    }
  }
);

// 創建Slice
const travelsSlice = createSlice({
  name: 'travels',
  initialState,
  reducers: {
    clearTravelError: (state) => {
      state.error = null;
    },
    setCurrentTravel: (state, action: PayloadAction<TravelDetail | null>) => {
      state.currentTravel = action.payload;
    },
    clearCurrentTravel: (state) => {
      state.currentTravel = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getMyTravels
      .addCase(getMyTravels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMyTravels.fulfilled, (state, action) => {
        state.loading = false;
        state.travels = action.payload;
      })
      .addCase(getMyTravels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getTravelDetail
      .addCase(getTravelDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTravelDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTravel = action.payload;
      })
      .addCase(getTravelDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // createTravel
      .addCase(createTravel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTravel.fulfilled, (state, action) => {
        state.loading = false;
        state.travels = [action.payload, ...state.travels];
      })
      .addCase(createTravel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateTravel
      .addCase(updateTravel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTravel.fulfilled, (state, action) => {
        state.loading = false;
        // 更新当前拼游信息
        if (state.currentTravel && state.currentTravel.id === action.payload.id) {
          state.currentTravel = { ...state.currentTravel, ...action.payload };
        }
        // 更新拼游列表中的信息
        const index = state.travels.findIndex(travel => travel.id === action.payload.id);
        if (index !== -1) {
          state.travels[index] = { ...state.travels[index], ...action.payload };
        }
      })
      .addCase(updateTravel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getVisibleTravels
      .addCase(getVisibleTravels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVisibleTravels.fulfilled, (state, action) => {
        state.loading = false;
        state.visibleTravels = action.payload;
      })
      .addCase(getVisibleTravels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // requestToJoinTravel
      .addCase(requestToJoinTravel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestToJoinTravel.fulfilled, (state, action) => {
        state.loading = false;
        // 根据操作类型更新状态
        const result = action.payload;
        const travelId = result.data.group_id;
        const travelIndex = state.visibleTravels.findIndex(t => t.id === travelId);
        
        if (travelIndex !== -1) {
          if (result.type === 'joined') {
            // 直接加入成功，标记为已加入
            (state.visibleTravels[travelIndex] as any).is_member = true;
          } else if (result.type === 'request') {
            // 提交申请成功，标记为有待处理申请
            (state.visibleTravels[travelIndex] as any).has_pending_request = true;
          }
        }
      })
      .addCase(requestToJoinTravel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getTravelJoinRequests
      .addCase(getTravelJoinRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTravelJoinRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.joinRequests = action.payload;
      })
      .addCase(getTravelJoinRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // handleJoinRequest
      .addCase(handleJoinRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleJoinRequest.fulfilled, (state, action) => {
        state.loading = false;
        // 从申请列表中移除已处理的申请
        state.joinRequests = state.joinRequests.filter(req => req.id !== action.payload.id);
      })
      .addCase(handleJoinRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteTravel
      .addCase(deleteTravel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTravel.fulfilled, (state, action) => {
        state.loading = false;
        const deletedTravelId = action.payload.travelId;
        // 从拼游列表中移除已删除的项目
        state.travels = state.travels.filter(travel => travel.id !== deletedTravelId);
        state.visibleTravels = state.visibleTravels.filter(travel => travel.id !== deletedTravelId);
        // 如果当前查看的是被删除的项目，清空当前项目
        if (state.currentTravel?.id === deletedTravelId) {
          state.currentTravel = null;
        }
      })
      .addCase(deleteTravel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // markTravelAsCompleted
      .addCase(markTravelAsCompleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markTravelAsCompleted.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTravel = action.payload;
        // 更新当前拼游信息
        if (state.currentTravel && state.currentTravel.id === updatedTravel.id) {
          state.currentTravel = { ...state.currentTravel, ...updatedTravel };
        }
        // 更新拼游列表中的信息
        const index = state.travels.findIndex(travel => travel.id === updatedTravel.id);
        if (index !== -1) {
          state.travels[index] = { ...state.travels[index], ...updatedTravel };
        }
      })
      .addCase(markTravelAsCompleted.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 监听邀请响应，如果接受了邀请，标记需要刷新拼游列表
      .addCase(respondToGroupInvitation.fulfilled, (state, action) => {
        const result = action.payload as any;
        if (result.shouldRefreshGroups) {
          console.log('检测到邀请接受，将在下次访问时刷新拼游列表');
          // 这里我们可以设置一个flag，或者直接在UI层面处理
          // 暂时通过console提示，实际的刷新由UI层面的监听器处理
        }
      })
      // POI功能已移除
      // getTravelTodos
      .addCase(getTravelTodos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTravelTodos.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTravelTodos = action.payload;
      })
      .addCase(getTravelTodos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTravelError, setCurrentTravel, clearCurrentTravel } = travelsSlice.actions;

// 为了兼容性，保留原有的 Group 相关类型导出
export type Group = Travel;
export type GroupMember = TravelMember;
export type GroupDetail = TravelDetail;
export type GroupJoinRequest = TravelJoinRequest;
export type GroupsState = TravelsState;

// 为了兼容性，保留原有的方法名导出
export const getMyGroups = getMyTravels;
export const getGroupDetail = getTravelDetail;
export const createGroup = createTravel;
export const getVisibleGroups = getVisibleTravels;
export const requestToJoinGroup = requestToJoinTravel;
export const getGroupJoinRequests = getTravelJoinRequests;
export const updateGroup = updateTravel;
export const clearGroupError = clearTravelError;
export const setCurrentGroup = setCurrentTravel;
export const clearCurrentGroup = clearCurrentTravel;

export default travelsSlice.reducer;
